(function (global) {
  "use strict";

  const ITEM_TEMPLATE_START = "<!-- item-template-start -->";
  const ITEM_TEMPLATE_END = "<!-- item-template-end -->";
  const DEFAULT_BATCH_SIZE = 20;
  const DEFAULT_TIMEOUT_MS = 10000;

  function extractItemTemplate(html) {
    const start = html.indexOf(ITEM_TEMPLATE_START);
    const end = html.indexOf(ITEM_TEMPLATE_END);

    if (start < 0 || end < 0 || end < start) {
      throw new Error("Invalid item-template section response");
    }

    return html.slice(start + ITEM_TEMPLATE_START.length, end).trim();
  }

  function createSectionUrl(sectionPath, handles) {
    const url = new URL(sectionPath, global.location.origin);
    url.search = "";
    url.hash = "";
    url.searchParams.set("section_id", "item-template");
    url.searchParams.set("q", handles.join(","));
    return url.toString();
  }

  function requestedPageSize(url, totalResults) {
    const requestUrl = new URL(url, global.location.origin);
    const page = Number(requestUrl.searchParams.get("page"));
    const limit = Number(requestUrl.searchParams.get("limit"));
    if (!Number.isInteger(page) || page < 1 || !Number.isInteger(limit) || limit < 1) {
      throw new Error("Search request is missing valid pagination");
    }
    return Math.min(limit, Math.max(totalResults - (page - 1) * limit, 0));
  }

  function countTiles(html) {
    const classPattern = /\bclass\s*=\s*(["'])([^"']*)\1/g;
    let count = 0;
    let match;
    while ((match = classPattern.exec(html))) {
      if (match[2].split(/\s+/).includes("tile-grid__tile")) count += 1;
    }
    return count;
  }

  function validateSearchPayload(payload, requestUrl) {
    if (
      !payload ||
      !Array.isArray(payload.results) ||
      !Number.isInteger(payload.totalResults) ||
      payload.totalResults < 0 ||
      !Number.isInteger(payload.totalPages) ||
      payload.totalPages < 0
    ) {
      throw new Error("Invalid search JSON response");
    }

    const handles = payload.results.map(function (result) {
      return result && typeof result.handle === "string" ? result.handle.trim() : "";
    });

    if (handles.some(function (handle) { return !handle; })) {
      throw new Error("Search JSON response is missing a product handle");
    }

    if (
      (!handles.length && (payload.totalResults !== 0 || payload.totalPages !== 0)) ||
      (handles.length && (payload.totalResults === 0 || payload.totalPages === 0))
    ) {
      throw new Error("Search JSON response has inconsistent pagination");
    }

    if (handles.length !== requestedPageSize(requestUrl, payload.totalResults)) {
      throw new Error("Search JSON response has an incomplete result page");
    }

    return {
      handles: handles,
      totalResults: payload.totalResults,
      totalPages: payload.totalPages
    };
  }

  function parseCount(response, name) {
    const raw = response.headers.get(name);
    if (raw === null || String(raw).trim() === "") return null;
    const value = Number(raw);
    return Number.isInteger(value) && value >= 0 ? value : null;
  }

  async function loadPrimaryPage(options) {
    const fetchImpl = options.fetchImpl || global.fetch.bind(global);
    const timeoutMs = options.timeoutMs || DEFAULT_TIMEOUT_MS;
    const controller = new AbortController();
    const abortFromCaller = function () { controller.abort(); };

    if (options.signal) {
      if (options.signal.aborted) controller.abort();
      else options.signal.addEventListener("abort", abortFromCaller, { once: true });
    }

    const timeout = global.setTimeout(function () { controller.abort(); }, timeoutMs);

    try {
      const response = await fetchImpl(options.htmlUrl, { signal: controller.signal });
      if (!response.ok) throw new Error("Theme HTML request failed: " + response.status);

      const html = await response.text();
      const totalResults = parseCount(response, "X-API-ResultCount");
      const totalPages = parseCount(response, "X-API-PageCount");
      const visibleHtml = html.replace(/<!--[\s\S]*?-->/g, "").trim();

      if (totalResults === 0 && totalPages === 0 && !visibleHtml) {
        return { html: "", totalResults: 0, totalPages: 0, source: "primary" };
      }

      const expectedTiles = totalResults === null
        ? null
        : requestedPageSize(options.htmlUrl, totalResults);
      const renderedTiles = countTiles(html);
      if (
        totalResults === null ||
        totalPages === null ||
        totalResults === 0 ||
        totalPages === 0 ||
        !visibleHtml ||
        renderedTiles === 0 ||
        renderedTiles > expectedTiles
      ) {
        throw new Error("Theme HTML response was empty or invalid");
      }

      return {
        html: html,
        totalResults: totalResults,
        totalPages: totalPages,
        source: "primary"
      };
    } finally {
      global.clearTimeout(timeout);
      if (options.signal) options.signal.removeEventListener("abort", abortFromCaller);
    }
  }

  async function loadPage(options) {
    const fetchImpl = options.fetchImpl || global.fetch.bind(global);
    const batchSize = options.batchSize || DEFAULT_BATCH_SIZE;
    const timeoutMs = options.timeoutMs || DEFAULT_TIMEOUT_MS;
    const controller = new AbortController();
    const abortFromCaller = function () { controller.abort(); };

    if (options.signal) {
      if (options.signal.aborted) controller.abort();
      else options.signal.addEventListener("abort", abortFromCaller, { once: true });
    }

    const timeout = global.setTimeout(function () { controller.abort(); }, timeoutMs);

    try {
      const searchResponse = await fetchImpl(options.jsonUrl, { signal: controller.signal });
      if (!searchResponse.ok) throw new Error("Search JSON request failed: " + searchResponse.status);

      const search = validateSearchPayload(await searchResponse.json(), options.jsonUrl);
      if (!search.handles.length) {
        if (search.totalResults !== 0) throw new Error("Search JSON returned a count without handles");
        return { html: "", totalResults: 0, totalPages: search.totalPages };
      }

      const batches = [];
      for (let index = 0; index < search.handles.length; index += batchSize) {
        batches.push(search.handles.slice(index, index + batchSize));
      }

      const renderedBatches = await Promise.all(batches.map(async function (handles) {
        const sectionResponse = await fetchImpl(createSectionUrl(options.sectionPath, handles), {
          signal: controller.signal
        });
        if (!sectionResponse.ok) throw new Error("Item-template request failed: " + sectionResponse.status);
        const fragment = extractItemTemplate(await sectionResponse.text());
        const visibleFragment = fragment.replace(/<!--[\s\S]*?-->/g, "").trim();
        const tileCount = countTiles(fragment);
        if ((visibleFragment && tileCount === 0) || tileCount > handles.length) {
          throw new Error("Item-template response contained invalid markup");
        }
        return { fragment: fragment, tileCount: tileCount };
      }));

      const html = renderedBatches.map(function (batch) { return batch.fragment; }).join("");
      const renderedTileCount = renderedBatches.reduce(function (total, batch) {
        return total + batch.tileCount;
      }, 0);
      if (renderedTileCount === 0 && search.totalResults > 0) {
        throw new Error("Item-template response contained no products");
      }

      return {
        html: html,
        totalResults: search.totalResults,
        totalPages: search.totalPages
      };
    } finally {
      global.clearTimeout(timeout);
      if (options.signal) options.signal.removeEventListener("abort", abortFromCaller);
    }
  }

  global.NCThemeSearch = Object.freeze({
    createSectionUrl: createSectionUrl,
    extractItemTemplate: extractItemTemplate,
    loadPrimaryPage: loadPrimaryPage,
    loadPage: loadPage,
    validateSearchPayload: validateSearchPayload
  });

  if (!global.customElements) return;

  global.customElements.whenDefined("nc-tile-grid").then(function () {
    if (global.customElements.get("nc-search-tile-grid")) return;

    const TileGrid = global.customElements.get("nc-tile-grid");

    class SearchTileGrid extends TileGrid {
      get searchErrorBox() {
        return this.querySelector(".tile-grid__search-error");
      }

      get searchRetryButton() {
        return this.querySelector(".btn-search-retry");
      }

      readyCallback() {
        super.readyCallback();
        if (this.searchRetryButton) {
          this.setEventListener("click", function () {
            const retry = this.searchRetryRequest || { page: this.current.page, replaceContent: true };
            this.current.page = retry.page;
            this.fetchPage(retry.replaceContent);
          }.bind(this), this.searchRetryButton);
        }
      }

      createHistoryUrl(state) {
        const currentUrl = new URL(global.location.href);
        Array.from(currentUrl.searchParams.keys()).forEach(function (key) {
          if (
            key === "page" ||
            key === "s_page" ||
            key === "s_sort" ||
            key === "type" ||
            key === "q" ||
            key.indexOf("f_") === 0
          ) {
            currentUrl.searchParams.delete(key);
          }
        });

        if (state.isDefaultBehavior) {
          currentUrl.searchParams.set("page", String(state.page));
        } else {
          currentUrl.searchParams.set("s_page", String(state.page));
          currentUrl.searchParams.set("s_sort", state.sort || "");
          Object.keys(state.filters).forEach(function (name) {
            state.filters[name].forEach(function (value) {
              currentUrl.searchParams.append("f_" + name, value);
            });
          });
        }

        if (state.terms) {
          currentUrl.searchParams.set("type", "product");
          currentUrl.searchParams.set("q", state.terms);
        }

        currentUrl.search = Array.from(currentUrl.searchParams.entries()).map(function (entry) {
          return encodeURIComponent(entry[0]) + "=" + encodeURIComponent(entry[1]);
        }).join("&");
        return currentUrl.toString();
      }

      replaceHistory(state) {
        const historyState = Object.assign({}, global.history.state || {}, { page: state.page });
        global.history.replaceState(historyState, "", this.createHistoryUrl(state));
      }

      preLoadNextPage() {
        if (this.searchPreloadController) this.searchPreloadController.abort();
        const next = Object.assign({}, this.current, { page: this.current.page + 1 });
        const query = this.prepareQuery(next);
        this.searchPreloadController = new AbortController();
        global.NCThemeSearch.loadPrimaryPage({
          htmlUrl: query.queryUrl,
          signal: this.searchPreloadController.signal
        }).then(function (result) {
          if (result.totalResults > 0) this.preLoadedPage = { url: query.queryUrl, result: result };
        }.bind(this)).catch(function () {});
      }

      disconnectedCallback() {
        if (this.searchController) this.searchController.abort();
        if (this.searchPreloadController) this.searchPreloadController.abort();
        super.disconnectedCallback();
      }

      fetchPage(replaceContent = true) {
        const query = this.prepareQuery(this.current);
        const requestPage = this.current.page;
        const requestSequence = (this.searchRequestSequence || 0) + 1;

        this.searchRequestSequence = requestSequence;
        if (this.searchController) this.searchController.abort();
        if (this.searchPreloadController) this.searchPreloadController.abort();
        this.searchController = new AbortController();
        this.loader.classList.remove("hidden");
        this.tileContainer.setAttribute("aria-busy", "true");
        this.dataset.searchState = "loading";
        if (this.searchErrorBox) this.searchErrorBox.classList.add("hidden");
        this.replaceHistory(this.current);
        this.renderPagination();

        const jsonUrl = query.queryUrl.replace(
          "/api/search/query.html?scope=theme&",
          "/api/search?"
        );
        const preloaded = this.preLoadedPage && this.preLoadedPage.url === query.queryUrl
          ? this.preLoadedPage.result
          : null;
        this.preLoadedPage = null;
        const request = preloaded
          ? Promise.resolve(preloaded)
          : global.NCThemeSearch.loadPrimaryPage({
            htmlUrl: query.queryUrl,
            signal: this.searchController.signal
          }).catch(function () {
            return global.NCThemeSearch.loadPage({
              jsonUrl: jsonUrl,
              sectionPath: this.baseUrl || global.location.pathname,
              signal: this.searchController.signal
            });
          }.bind(this));

        return request.then(function (result) {
          if (requestSequence !== this.searchRequestSequence) return;

          this.current.pageCount = result.totalPages;
          this.itemCountIndicator.textContent = String(result.totalResults);
          this.renderContent(result.html, replaceContent);
          this.tileContainer.classList.remove("hidden");
          this.noResultBox.classList.toggle("hidden", result.totalResults !== 0);
          this.dataset.searchState = result.totalResults === 0 ? "empty" : "ready";
          this.searchRetryRequest = null;
          if (this.searchErrorBox) this.searchErrorBox.classList.add("hidden");
          if (this.current.page < this.current.pageCount) this.preLoadNextPage();
          this.renderPagination();
        }.bind(this)).catch(function (error) {
          if (requestSequence !== this.searchRequestSequence) return;

          if (!replaceContent && this.current.page === requestPage) {
            this.current.page = Math.max(1, requestPage - 1);
            this.replaceHistory(this.current);
          }

          this.noResultBox.classList.add("hidden");
          this.dataset.searchState = "error";
          if (replaceContent) this.itemCountIndicator.textContent = "-";
          this.searchRetryRequest = { page: requestPage, replaceContent: replaceContent };
          if (!this.tileContainer.querySelector(".tile-grid__tile:not(.nc-search-skeleton)")) {
            this.tileContainer.innerHTML = "";
          }
          if (this.searchErrorBox) this.searchErrorBox.classList.remove("hidden");
          this.renderPagination();
          if (error && error.name !== "AbortError") global.console.error("Theme search failed", error);
        }.bind(this)).finally(function () {
          if (requestSequence !== this.searchRequestSequence) return;
          this.loader.classList.add("hidden");
          this.tileContainer.classList.remove("hidden");
          this.tileContainer.setAttribute("aria-busy", "false");
        }.bind(this));
      }
    }

    global.customElements.define("nc-search-tile-grid", SearchTileGrid);
  });
})(window);
